/**
 * Small self-contained helpers for the admin clients feature (the web
 * `@shared/utils` cn and the Design D display helpers).
 */

/** Join truthy class names (stand-in for the web `cn`). */
export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

/* ── Design D helpers ──────────────────────────────────────────────────────
 * The artboards print a client's identity three times (avatar monogram, table
 * cell, detail header) and always from the same two name fields, so the rules
 * live here once rather than in each component.
 */

/** Best display name for a client — falls back through the fields the API fills. */
export function clientName(user: {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
}): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (user.displayName) return user.displayName;
  return user.email ?? '';
}

/** Up to two uppercase letters for the avatar circle ("PB" for P3 Bidder). */
export function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => /\p{L}|\p{N}/u.test(part));
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/** `16.08.2026` — the artboard's registration-date format. */
export function formatShortDate(value: string | undefined | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** `12 500` — thousands separated by a space, the artboard's money format. */
export function formatAmount(value: number | undefined | null): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('uk-UA').format(Math.round(value));
}

/**
 * Leading characters a spreadsheet reads as the start of a formula (OWASP
 * "CSV injection"). A client controls their own name, so a name such as
 * `=HYPERLINK("https://evil", "click")` would run when an operator opens the
 * export in Excel. Quoting does not help — Excel strips the quotes first.
 */
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

/** Prefix a formula-looking cell with `'` so the spreadsheet shows it as text. */
export function neutralizeCsvFormula(cell: string): string {
  return FORMULA_TRIGGER.test(cell) ? `'${cell}` : cell;
}

/** RFC-4180-ish CSV: neutralise formulas, quote every field, double the inner quotes. */
export function toCsv(rows: string[][]): string {
  return rows
    .map((row) => row.map((cell) => `"${neutralizeCsvFormula(cell).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
}

/** Hand the operator a generated file without a server round-trip. */
export function downloadTextFile(fileName: string, content: string, mime = 'text/csv'): void {
  // A BOM, so Excel opens the Cyrillic columns as UTF-8 instead of cp1251.
  const blob = new Blob(['\uFEFF' + content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
